import { css, Global } from '@emotion/react';
import { useEffect, useState } from 'react';

import {
  GainSelfAbility,
  getGainSelfCardAbility,
} from '@mythos/game/CardAbilities';
import { CardAffinity, CardType } from '@mythos/game/CardTypes';
import { CardDef } from '@mythos/game/GameModel';
import {
  ageString,
  isCardMercenary,
  isCardScavenge,
} from '@mythos/game/Utility';
import { useShallow } from 'zustand/shallow';
import { SymbolWithNumber } from './CardHelpers';
import { GainSelfEffect } from './CardView';
import {
  CardAffinityToFrameColor,
  CardTypeToSymbolColor,
} from './ColorPalette';
import { useGameDefinitionsStore } from './stores/gameDefinitionsStore';
import Symbols, { CardTypeToSymbol, SymbolView } from './Symbols';

const BASIC_FRAME_COLOR = CardAffinityToFrameColor[CardAffinity.Basic];

// Dimensions in inches for correct physical print size.
const SLOT_WIDTH = '2.75in';
const SLOT_HEIGHT = '3.75in';
const SLOT_GAP = '0.35in';
const CARD_BORDER_RADIUS = '0.125in';

// Scale factor for shared components (SymbolWithNumber, GainSelfEffect)
// that use the card base unit system internally
const SYMBOL_SCALE = 2.5;

function BasicBoardPage() {
  const { gameDefinitions, fetchGameDefinitions } = useGameDefinitionsStore(
    useShallow((state) => ({
      gameDefinitions: state.gameDefinitions,
      fetchGameDefinitions: state.fetchGameDefinitions,
    })),
  );
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    fetchGameDefinitions();
  }, [fetchGameDefinitions]);

  if (!gameDefinitions) {
    return <div>Loading...</div>;
  }

  const { cardDefs } = gameDefinitions;

  const scavengeCards = cardDefs
    .filter((c) => isCardScavenge(c))
    .sort((a, b) => a.age - b.age);
  const mercenaryCards = cardDefs
    .filter((c) => isCardMercenary(c) && c.age === 1)
    .sort((a, b) => a.cost - b.cost);

  return (
    <>
      <Global styles={PrintStyles.global} />
      <div css={PageStyles.pageContainer}>
        <div css={PageStyles.boardContainer}>
          <div css={PageStyles.boardZoom} style={{ zoom }}>
            <BasicBoardView
              scavengeCards={scavengeCards}
              mercenaryCards={mercenaryCards}
            />
          </div>
        </div>
        <div css={PageStyles.controlsPanel} className="no-print">
          <div css={PageStyles.controlsTitle}>Controls</div>
          <div css={PageStyles.controlGroup}>
            <label css={PageStyles.controlLabel}>
              Zoom: {zoom.toFixed(1)}x
            </label>
            <input
              type="range"
              min="0.3"
              max="2"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              css={PageStyles.slider}
            />
          </div>
          <button css={PageStyles.printButton} onClick={() => window.print()}>
            Print / PDF
          </button>
        </div>
      </div>
    </>
  );
}

function BasicBoardView(props: {
  scavengeCards: CardDef[];
  mercenaryCards: CardDef[];
}) {
  const { scavengeCards, mercenaryCards } = props;

  return (
    <div id="basic-board" css={BoardStyles.board}>
      <div css={BoardStyles.columnsRow}>
        <div css={BoardStyles.slotColumn}>
          <ScavengeReferencePanel cards={scavengeCards} />
          <CardSlot name="Scavenge" cardType={CardType.Event} />
        </div>
        <div css={BoardStyles.slotColumn}>
          <div css={BoardStyles.titleBar}>
            <span css={BoardStyles.title}>Basic Row</span>
            <span css={BoardStyles.subtitle}>
              Losers of a clash gain a basic card
            </span>
          </div>
          <CardSlot name="Settlement" cardType={CardType.Building} />
        </div>
        <div css={BoardStyles.slotColumn}>
          <MercenaryReferencePanel cards={mercenaryCards} />
          <CardSlot name="Mercenary" cardType={CardType.Unit} />
        </div>
      </div>
    </div>
  );
}

function CardSlot(props: { name: string; cardType: CardType }) {
  const symbol = CardTypeToSymbol[props.cardType];
  const typeColor = CardTypeToSymbolColor[props.cardType];

  return (
    <div css={SlotStyles.slotContainer}>
      <div css={SlotStyles.label}>
        <SymbolView
          symbol={symbol}
          css={SlotStyles.typeIcon}
          style={{ color: typeColor }}
        />
        <span style={{ color: typeColor }}>{props.name}</span>
      </div>
      <div css={SlotStyles.outline}>
        <SymbolView
          symbol={symbol}
          css={SlotStyles.centerIcon}
          style={{ color: typeColor }}
        />
      </div>
    </div>
  );
}

function ScavengeReferencePanel(props: { cards: CardDef[] }) {
  return (
    <div css={RefStyles.panel}>
      <div css={RefStyles.columns}>
        <div css={RefStyles.rowLabel}>Age</div>
        {props.cards.map((card) => (
          <div key={card.defID} css={RefStyles.column}>
            <div css={RefStyles.columnLabel}>{ageString(card.age)}</div>
            <div css={RefStyles.columnItems} style={{ zoom: SYMBOL_SCALE }}>
              <div css={RefStyles.gainNudge}>
                <GainSelfEffect
                  ability={getGainSelfCardAbility(card)}
                  borderColor={BASIC_FRAME_COLOR}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MercenaryReferencePanel(props: { cards: CardDef[] }) {
  const variants = props.cards.map((card, index) => ({
    label: String.fromCharCode(65 + index),
    card,
  }));

  return (
    <div css={RefStyles.panel}>
      <div css={RefStyles.columns}>
        <div css={RefStyles.rowLabel}>Choice</div>
        {variants.map((variant) => (
          <div key={variant.label} css={RefStyles.column}>
            <div css={RefStyles.columnLabel}>{variant.label}</div>
            <div css={RefStyles.columnItems} style={{ zoom: SYMBOL_SCALE }}>
              <div css={RefStyles.costNudge}>
                <SymbolWithNumber
                  symbol={Symbols.GOLD}
                  number={variant.card.cost}
                  height={16}
                  fontSize={9}
                />
              </div>
              <div css={RefStyles.gainNudge}>
                <GainSelfEffect
                  ability={
                    getGainSelfCardAbility(variant.card) ??
                    new GainSelfAbility('0M')
                  }
                  borderColor={BASIC_FRAME_COLOR}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Styles ---

const PrintStyles = {
  global: css`
    @media print {
      @page {
        size: 11in 8.5in;
        margin: 0;
      }
      html,
      body {
        margin: 0 !important;
        padding: 0 !important;
        width: 11in !important;
        height: 8.5in !important;
        overflow: hidden !important;
        background: white !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
};

const PageStyles = {
  pageContainer: css({
    display: 'flex',
    flexDirection: 'row',
    minHeight: '100vh',
  }),
  boardContainer: css({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    '@media print': {
      flex: 'none',
      width: '11in',
      height: '8.5in',
      padding: 0,
    },
  }),
  boardZoom: css({
    '@media print': {
      zoom: 'normal !important',
    },
  }),
  controlsPanel: css({
    width: 200,
    backgroundColor: '#1a1a1a',
    borderLeft: '1px solid #333',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  }),
  controlsTitle: css({
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    borderBottom: '1px solid #333',
    paddingBottom: 8,
  }),
  controlGroup: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  }),
  controlLabel: css({
    color: '#ccc',
    fontSize: 13,
  }),
  slider: css({
    width: '100%',
    cursor: 'pointer',
  }),
  printButton: css({
    padding: '8px 16px',
    backgroundColor: '#444',
    color: '#fff',
    border: '1px solid #666',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    '&:hover': {
      backgroundColor: '#555',
    },
  }),
};

const BoardStyles = {
  board: css({
    backgroundColor: 'rgb(50, 38, 32)',
    backgroundImage:
      'linear-gradient(135deg, rgb(60, 48, 40) 0%, rgb(45, 34, 28) 100%)',
    borderRadius: '0.2in',
    padding: '0.15in 0.25in 0.3in',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125in',
    border: `0.05in solid ${BASIC_FRAME_COLOR}`,
  }),
  titleBar: css({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.02in',
    paddingTop: '0.06in',
  }),
  title: css({
    fontSize: '0.34in',
    fontWeight: 'bold',
    color: 'rgb(215, 200, 180)',
    fontFamily: 'Mythos-CardTitle',
    letterSpacing: '0.05in',
    textTransform: 'uppercase',
  }),
  subtitle: css({
    fontSize: '0.14in',
    color: 'rgb(150, 130, 110)',
    fontStyle: 'italic',
  }),
  columnsRow: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: SLOT_GAP,
  }),
  slotColumn: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.1in',
  }),
};

const SlotStyles = {
  slotContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.07in',
  }),
  label: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.07in',
    fontSize: '0.18in',
    fontWeight: 'bold',
    fontFamily: 'Mythos-CardTitle',
  }),
  typeIcon: css({
    height: '0.2in',
    width: 'auto',
  }),
  outline: css({
    width: SLOT_WIDTH,
    height: SLOT_HEIGHT,
    borderRadius: CARD_BORDER_RADIUS,
    border: `0.035in dashed rgba(150, 111, 93, 0.5)`,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  centerIcon: css({
    height: '1.25in',
    width: 'auto',
    opacity: 0.15,
  }),
};

const RefStyles = {
  panel: css({
    position: 'relative',
    width: SLOT_WIDTH,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '0.1in',
    padding: '0.07in 0.1in',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.05in',
  }),
  rowLabel: css({
    position: 'absolute',
    left: '0.08in',
    top: '0.05in',
    fontSize: '0.126in',
    color: 'rgb(130, 115, 100)',
    fontFamily: 'Mythos-CardTitle',
    fontStyle: 'italic',
    textTransform: 'uppercase',
  }),
  columns: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '0.096in',
  }),
  column: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  columnLabel: css({
    fontSize: '0.15in',
    fontWeight: 'bold',
    color: 'rgb(200, 180, 160)',
    marginBottom: '0.05in',
    fontFamily: 'Mythos-CardTitle',
  }),
  columnItems: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > *:not(:first-child)': {
      marginTop: -2,
    },
  }),
  costNudge: css({
    '& span': {
      marginTop: 1,
    },
  }),
  gainNudge: css({
    '& span': {
      marginTop: 0.5,
    },
  }),
};

export default BasicBoardPage;
